import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../backend/firebase';
import HandAnalyzer, { RoomAnalyzer } from '../components/HandAnalyzer';
import SEO from '../components/SEO';
import { createPortal } from 'react-dom';
import { useWindowSize } from '../hooks/useWindowSize';
import { generateBoardLIN, generateLinFileContent, downloadLinFile } from '../utils/dealer';
import { parseLin, parseAliases } from '../utils/linParser';

// --- HELPER: Derive Contract from BBO Auction ---
const deriveContractFromBBO = (bidding, dealerStr) => {
    if (!bidding || bidding.length === 0) return "Pass";
    const SEATS = ['W', 'N', 'E', 'S'];
    const dealerMap = { '1': 'S', '2': 'W', '3': 'N', '4': 'E' };
    const dealerSeat = dealerMap[dealerStr] || 'S';
    
    let lastBid = null; let lastBidSeat = null; let dbl = '';
    let turn = SEATS.indexOf(dealerSeat) !== -1 ? SEATS.indexOf(dealerSeat) : 3;
    let firstBids = { NS: {}, EW: {} };

    for (let call of bidding) {
        const c = call.split('!')[0].toUpperCase().trim();
        const side = (turn === 1 || turn === 3) ? 'NS' : 'EW';
        
        if (c === 'X' || c === 'D') dbl = 'x';
        else if (c === 'XX' || c === 'R') dbl = 'xx';
        else if (c !== 'P' && c !== 'PASS') {
            const match = c.match(/^([1-7])([CDHSN])/);
            if (match) {
                lastBid = { level: match[1], suit: match[2] };
                lastBidSeat = SEATS[turn]; dbl = '';
                if (!firstBids[side][match[2]]) firstBids[side][match[2]] = SEATS[turn];
            }
        }
        turn = (turn + 1) % 4;
    }
    if (!lastBid) return "Pass";
    const winningSide = (lastBidSeat === 'N' || lastBidSeat === 'S') ? 'NS' : 'EW';
    const declarer = firstBids[winningSide][lastBid.suit];
    return `${lastBid.level}${lastBid.suit} ${declarer}${dbl}`;
};

const SingleHandViewer = ({ details, boardNum, contractStr, onSaveLabHand, label }) => {
    const { width, height } = useWindowSize();
    const [fullscreen, setFullScreen] = useState(false);
    const [showDdTable, setShowDdTable] = useState(false);

    const roomScale = useMemo(() => {
        if (!fullscreen || !width || !height) return 1;
        // Exact same math as HandAnalyzer
        return Math.min((width ) / 550, height / 570);
    }, [fullscreen, width, height]);

    useEffect(() => {
        document.body.style.overflow = fullscreen ? 'hidden' : 'auto';
        return () => { document.body.style.overflow = 'auto'; };
    }, [fullscreen]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setFullScreen(false);
            }
        };

        if (fullscreen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = 'auto';
            window.removeEventListener('keydown', handleKeyDown);
        }

        return () => { 
            document.body.style.overflow = 'auto'; 
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [fullscreen]);

    const content = (
        <div style={fullscreen ? {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            width: '100vw', height: '100vh',
            background: `linear-gradient(135deg, #2D2B3F 0%, #0D0E15 100%)`,
            zIndex: 999999, padding: '20px', boxSizing: 'border-box', overflowY: 'auto'
        } : { 
            padding: '15px 0', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center' 
        }}>
            {/* Top Toolbar matching HandAnalyzer */}
            {!fullscreen && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 10, background: fullscreen ? 'rgba(0, 0, 0, 0.4)' : 'transparent', padding: fullscreen ? '10px 15px' : '0 15px', borderRadius: 8, width: '100%', maxWidth: 1000, border: fullscreen ? '1px solid rgba(255,255,255,0.05)' : 'none', boxSizing: 'border-box' }}>
                    
                    <button onClick={() => setFullScreen(!fullscreen)} style={{ background: fullscreen ? 'rgba(108, 93, 211, 0.2)' : 'transparent', color: fullscreen ? '#fff' : '#A0AABF', border: `1px solid ${fullscreen ? '#6C5DD3' : 'rgba(255,255,255,0.2)'}`, borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 'bold', cursor: 'pointer', marginLeft: 'auto' }}>
                        {fullscreen ? 'Exit Full Screen' : 'Full Screen'}
                    </button>
                </div>
            )}

            <div style={{ width: '100%', maxWidth: fullscreen ? 'none' : 500, display: 'flex', justifyContent: 'center' }}>
                <RoomAnalyzer 
                    details={details} roomName={label} contractStr={contractStr} boardNum={boardNum} 
                    isActive={true} displayMode="visual" onRoomDD={true}
                    scale={roomScale} isFullscreen={fullscreen} showDdTable={showDdTable}
                    cardScale={fullscreen ? 1.35 : 1} tableScale={fullscreen ? 1.25 : 1}
                    onSaveLabHand={onSaveLabHand}
                />
            </div>
        </div>
    );

    return fullscreen ? createPortal(content, document.body) : content;
};

const HandLab = () => {
    const { 
        user, userRole, allUsers,
        createAnalysisBoard, updateAnalysisBoard, deleteAnalysisBoard, 
        inviteToAnalysisBoard, uninviteFromAnalysisBoard, subscribeToAnalysisLobby
    } = useData();
    
    const isAdmin = userRole?.role === 'admin';
    const { width, height } = useWindowSize();
    
    // --- STATE ---
    const [activeItem, setActiveItem] = useState(null); // The boardState object
    const [itemId, setItemId] = useState(null); // The Firebase document ID
    const [liveItems, setLiveItems] = useState([]); // Array of { id, docData, data }
    const [activeBoardPlay, setActiveBoardPlay] = useState(null); // Expanded row index

    // UI States
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isSharing, setIsSharing] = useState(false);
    const [shareQuery, setShareQuery] = useState('');

    // Setup Screen State (Single Board)
    const [setupNum, setSetupNum] = useState("1");
    const [setupName, setSetupName] = useState("New Analysis");
    const [bboLink, setBboLink] = useState("");

    // Setup Screen State (Batch Generator)
    const [genStart, setGenStart] = useState("1");
    const [genEnd, setGenEnd] = useState("8");
    const [genName, setGenName] = useState("New Pre-deals");
    const [isGenerating, setIsGenerating] = useState(false);

    // Setup Screen State (LIN Import)
    const [importLin, setImportLin] = useState("");
    const [importName, setImportName] = useState("");

    // Save State
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveName, setSaveName] = useState("");
    const [pendingData, setPendingData] = useState(null);
    const [confirmOverwrite, setConfirmOverwrite] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [filterMode, setFilterMode] = useState('all'); // 'all', 'mine', 'shared'
    const [impersonateEmail, setImpersonateEmail] = useState("");
    const [impersonateSearch, setImpersonateSearch] = useState("");
    const [isImpersonatingUI, setIsImpersonatingUI] = useState(false);

    const effectiveEmail = impersonateEmail || user?.email;

    // --- QUOTA TRACKING ---
    const mySavedCount = liveItems.filter(item => item.docData?.createdBy === effectiveEmail).length;
    const canSave = isAdmin || mySavedCount < 25;

    // --- LOBBY: Fetch available items ---
    useEffect(() => {
        if (!effectiveEmail) return;
        
        // 🚨 OPTIMIZATION: By passing a spoofed user and hardcoding `isAdmin` to false,
        // we force Firebase to ONLY pull documents tied to the effectiveEmail.
        const spoofedUser = { email: effectiveEmail };
        
        const unsub = subscribeToAnalysisLobby(spoofedUser, false, (snap) => {
            const items = [];
            snap.forEach(d => {
                const docData = d.data();
                const state = docData.boardState; 
                if (state) {
                    items.push({ id: d.id, data: state, docData: docData });
                }
            });
            items.sort((a, b) => (b.docData.lastUpdateTimestamp || 0) - (a.docData.lastUpdateTimestamp || 0));
            setLiveItems(items);
        });
        return () => unsub();
    }, [effectiveEmail, subscribeToAnalysisLobby]);

    // const labScale = useMemo(() => {
    //     if (!width || !height) return 1;
    //     return Math.min(width / 640, height / 600);
    // }, [width, height]);

    const filteredItems = useMemo(() => {
        return liveItems.filter(item => {
            const isOwner = item.docData?.createdBy === effectiveEmail;
            if (filterMode === 'mine') return isOwner;
            if (filterMode === 'shared') return !isOwner;
            return true; 
        });
    }, [liveItems, filterMode, effectiveEmail]);

    // Admin Impersonation Autocomplete
    const impersonateSearchResults = useMemo(() => {
        if (!impersonateSearch || !allUsers) return [];
        return allUsers.filter(u => 
            u.email?.toLowerCase().includes(impersonateSearch.toLowerCase()) || 
            (u.name && u.name.toLowerCase().includes(impersonateSearch.toLowerCase()))
        ).slice(0, 5); 
    }, [impersonateSearch, allUsers]);

    const WIN_COLOR = '#6cbd8c';
    const LOSE_COLOR = '#ed7666';
    const TIE_COLOR = '#6B7280';

    const formatContract = (contractStr) => {
        const containerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '60px', margin: '0 auto', fontWeight: 'bold' };
        if (!contractStr || contractStr.trim() === '' || contractStr.toUpperCase() === 'P' || contractStr.toUpperCase() === 'PASS') {
            return <div style={containerStyle}><span style={{color: '#8F9EB5'}}>Pass</span><span></span></div>;
        }
        const match = contractStr.trim().match(/^([1-7])([CDHSN])(.*)$/i);
        if (!match) return <div style={containerStyle}><span>{contractStr}</span><span></span></div>;
        
        const level = match[1];
        const suit = match[2].toUpperCase();
        const rest = match[3];
        const SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣', 'N': 'NT' };
        const COLORS = { 'S': '#c0cAdF', 'H': '#FF3366', 'D': '#FF3366', 'C': '#c0cAdF', 'N': '#c0cAdF' };
        
        let modifiers = rest;
        let resultSpan = <span></span>;

        const resultMatch = rest.match(/^(.*?)([-=+](?:\d+)?)$/);
        if (resultMatch) {
            modifiers = resultMatch[1]; 
            const resultText = resultMatch[2]; 
            const isWin = resultText.startsWith('=') || resultText.startsWith('+');
            resultSpan = <span style={{color: isWin ? WIN_COLOR : LOSE_COLOR}}>{resultText}</span>;
        }
        return (
            <div style={containerStyle}>
                <span style={{color:'#c0cAdF'}}>{level}<span style={{color: COLORS[suit]}}>{SYMBOLS[suit]}</span>{modifiers}</span>
                {resultSpan}
            </div>
        );
    };

    // Used to lookup the raw document (for the allowedUsers list)
    const activeItemDoc = useMemo(() => {
        return liveItems.find(i => i.id === itemId)?.docData;
    }, [liveItems, itemId]);

    // --- 1. SINGLE BOARD LOGIC (Original Local/Cloud) ---
    const parseBboLink = (urlStr) => {
        try {
            const cleanUrl = decodeURIComponent(urlStr.trim());
            const linMatch = cleanUrl.match(/lin=([^&]+)/);
            if (!linMatch) throw new Error("No LIN data found.");
            const lin = linMatch[1];

            const mdMatch = lin.match(/md\|([^|]+)\|/);
            let parsedLin = "3S876H876D876C6543,S5432H5432D5432C2,SAKQHAKQDAKQCAKQJ,SJT9HJT9DJT9CT987"; 
            let dealerStr = "3";
            if (mdMatch) {
                const rawMd = mdMatch[1];
                dealerStr = rawMd.charAt(0);
                parsedLin = dealerStr + rawMd.substring(1); 
            }

            const bidding = [...lin.matchAll(/mb\|([^|]+)\|/g)].map(m => m[1]);
            const play = [...lin.matchAll(/pc\|([^|]+)\|/g)].map(m => m[1]);
            const claim = (lin.match(/mc\|([^|]+)\|/) || [])[1] || "";

            const aliases = { N: "North", S: "South", E: "East", W: "West" };
            const pnMatch = lin.match(/pn\|([^|]+)\|/);
            if (pnMatch) {
                const names = pnMatch[1].split(',');
                if (names[0]) aliases.S = names[0];
                if (names[1]) aliases.W = names[1];
                if (names[2]) aliases.N = names[2];
                if (names[3]) aliases.E = names[3];
            }
            return { hands: parsedLin, bidding, play, claim, aliases, contractStr: deriveContractFromBBO(bidding, dealerStr) };
        } catch (error) { return null; }
    };

    const handleCreateLocal = () => {
        let state = {
            type: 'single', boardNum: setupNum || "1", boardName: setupName || "Local Analysis",
            details: { hands: "3S876H876D876C6543,S5432H5432D5432C2,SAKQHAKQDAKQCAKQJ,SJT9HJT9DJT9CT987", bidding: [], play: [], claim: "" },
            contractStr: "Pass", aliases: { N: "North", S: "South", E: "East", W: "West" }
        };
        if (bboLink.trim() !== "") {
            const bboData = parseBboLink(bboLink);
            if (bboData) state = { ...state, details: bboData, contractStr: bboData.contractStr, aliases: bboData.aliases };
        }
        setItemId(null); setActiveItem(state); setBboLink("");
    };

    const handleCreateNew = async () => {
        let state = {
            type: 'single', boardNum: setupNum || "1", boardName: setupName || "New Analysis",
            details: { hands: "3S876H876D876C6543,S5432H5432D5432C2,SAKQHAKQDAKQCAKQJ,SJT9HJT9DJT9CT987", bidding: [], play: [], claim: "" },
            contractStr: "Pass", aliases: { N: "North", S: "South", E: "East", W: "West" }
        };
        if (bboLink.trim() !== "") {
            const bboData = parseBboLink(bboLink);
            if (bboData) state = { ...state, details: bboData, contractStr: bboData.contractStr, aliases: bboData.aliases };
        }
        
        if (!canSave) return alert(`You have reached the 25 document limit (${mySavedCount}/25). Please delete an item from your lobby to save more.`);
        
        const newId = `board_${Date.now()}`;
        await createAnalysisBoard(newId, { boardState: state, allowedUsers: [user.email.toLowerCase()], createdBy: user.email, lastUpdateTimestamp: Date.now() });
        setItemId(newId);
        setActiveItem(state); setBboLink("");
    };

    const handleImportLin = async () => {
        if (!importLin) return;
        if (!canSave) return alert(`You have reached the 25 document limit (${mySavedCount}/25). Please delete an item to import more.`);
        
        try {
            const parsed = parseLin(importLin, parseAliases("")); 
            const boards = parsed.boards.map(b => ({ ...b, isVisible: true })); 

            const state = {
                type: 'collection', collectionType: 'match',
                boardName: importName || `${parsed.team1Name} vs ${parsed.team2Name}`,
                linTeam1: parsed.team1Name, linTeam2: parsed.team2Name,
                players: parsed.players, boards: boards
            };

            const newId = `col_${Date.now()}`;
            await createAnalysisBoard(newId, { boardState: state, allowedUsers: [user.email.toLowerCase()], createdBy: user.email, lastUpdateTimestamp: Date.now() });
            setImportLin("");
            // alert("Match imported successfully.");
        } catch (e) { alert("Parse Error: " + e.message); }
    };

    // --- 2. BATCH GENERATOR LOGIC ---
    const handleGenerateBatch = async () => {
        const start = parseInt(genStart); const end = parseInt(genEnd);
        if (isNaN(start) || isNaN(end) || start > end) return alert("Invalid range");

        setIsGenerating(true);
        let rawLin = "";
        const boards = [];

        for (let i = start; i <= end; i++) {
            const deal = generateBoardLIN(i);
            rawLin += deal.linString + "\n";
            boards.push({
                boardNum: deal.boardNum,
                isVisible: false, // 🔒 HIDDEN BY DEFAULT
                details: { hands: deal.internalHands, bidding: [], play: [], claim: "" },
                contractStr: "Pass"
            });
        }

        const state = {
            type: 'collection',
            collectionType: 'batch',
            boardName: genName || `Pre-deals ${start}-${end}`,
            rawLinFile: rawLin,
            boards: boards
        };

        const newId = `col_${Date.now()}`;
        try {
            await createAnalysisBoard(newId, { boardState: state, allowedUsers: [user.email.toLowerCase()], createdBy: user.email, lastUpdateTimestamp: Date.now() });
            // alert("Generated and hidden.");
        } catch (e) { alert("Failed to save."); }
        setIsGenerating(false);
    };

    // --- 4. SAVING & VISIBILITY LOGIC ---
    const toggleBoardVisibility = async (boardIndex) => {
        if (!itemId || activeItem.type !== 'collection') return;
        const updatedBoards = [...activeItem.boards];
        updatedBoards[boardIndex].isVisible = !updatedBoards[boardIndex].isVisible;
        const newState = { ...activeItem, boards: updatedBoards };
        
        await updateAnalysisBoard(itemId, { boardState: newState, lastUpdateTimestamp: Date.now() });
        setActiveItem(newState);
    };

    // Triggered strictly by the HandAnalyzer component passing its internal state out
    const handleManualSave = (linStr, bidding, play, contract, boardIndex = null) => {
        setPendingData({ linStr, bidding, play, contract, boardIndex });
        setSaveName(activeItem.boardName);
        setConfirmOverwrite(false);
        setShowSaveModal(true);
    };

    const executeSave = async () => {
        setIsSaving(true);
        try {
            const isOverwrite = saveName.trim() === activeItem.boardName;
            let newState;
            
            if (activeItem.type === 'collection') {
                const bIdx = pendingData.boardIndex;
                const updatedBoards = [...activeItem.boards];
                updatedBoards[bIdx] = {
                    ...updatedBoards[bIdx],
                    details: { hands: pendingData.linStr, bidding: pendingData.bidding, play: pendingData.play, claim: updatedBoards[bIdx].details?.claim || "" },
                    contractStr: pendingData.contract
                };
                newState = { ...activeItem, boardName: saveName.trim(), boards: updatedBoards };
            } else {
                newState = {
                    ...activeItem, boardName: saveName.trim(),
                    details: { hands: pendingData.linStr, bidding: pendingData.bidding, play: pendingData.play, claim: activeItem.details?.claim || "" },
                    contractStr: pendingData.contract
                };
            }

            if (isOverwrite && itemId) {
                // Overwriting an existing document doesn't cost quota
                await updateAnalysisBoard(itemId, { boardState: newState, lastUpdateTimestamp: Date.now() });
                setActiveItem(newState);
            } else {
                if (!canSave) {
                    setIsSaving(false);
                    return alert(`You have reached the 25 document limit (${mySavedCount}/25). Please delete an item to save a new copy.`);
                }
                const newId = `board_${Date.now()}`;
                await createAnalysisBoard(newId, { boardState: newState, allowedUsers: [user.email.toLowerCase()], createdBy: user.email, lastUpdateTimestamp: Date.now() });
                setItemId(newId); setActiveItem(newState);
            }
            setShowSaveModal(false);
        } catch (e) { alert(`Save failed: ${e.message}`); } finally { setIsSaving(false); }
    };

    // --- 5. SHARE LOGIC ---
    const filteredUsers = useMemo(() => {
        if (!shareQuery || !allUsers) return [];
        return allUsers.filter(u => u.email?.toLowerCase().includes(shareQuery.toLowerCase()) || (u.name && u.name.toLowerCase().includes(shareQuery.toLowerCase()))).slice(0, 5); 
    }, [shareQuery, allUsers]);

    const executeShareAction = async (targetEmail, isRevoke = false) => {
        try {
            if (isRevoke) {
                await uninviteFromAnalysisBoard(itemId, targetEmail);
                // alert(`Revoked access for ${targetEmail}`);
            } else {
                await inviteToAnalysisBoard(itemId, targetEmail);
                // alert(`Invited ${targetEmail}`);
            }
            setShareQuery(""); 
        } catch (e) { alert("Share action failed."); }
    };

    return (
        <div className="PageLayout" style={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
            <SEO title="Analysis Board" />
            
            <style>{`
                .unified-backdrop { background: #1A1D26; border: 1px solid rgba(255,255,255,0.05); box-shadow: 0 15px 40px rgba(0,0,0,0.6); border-radius: 16px; padding: 15px; width: 100%; display: flex; flex-direction: column; gap: 15px; max-height: 100%; min-height: 0; }
                .analysis-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 12px; padding: 15px; display: flex; flex-direction: column; gap: 10px; flex: 1; min-height: 0; }
                .lobby-grid { display: flex; gap: 20px; align-items: stretch; flex-wrap: wrap; }
                .board-btn { width: 100%; text-align: left; padding: 12px 16px; background: rgba(0,0,0,0.2); color: #E0E0E0; border: 1px solid rgba(255,255,255,0.05); border-radius: 8px; transition: all 0.2s ease; cursor: pointer; display: flex; flex-direction: column; gap: 4px; }
                .board-btn:hover { background: rgba(108, 93, 211, 0.15); border-color: rgba(108, 93, 211, 0.4); transform: translateY(-2px); }
                .create-btn { width: 100%; padding: 12px; background: #6C5DD3; color: #fff; border: none; border-radius: 8px; font-weight: bold; cursor: pointer; transition: all 0.2s; text-transform: uppercase; }
                .input-label { display: block; color: #8F9EB5; font-size: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
                .lab-table th { padding: 10px; text-align: left; color: #8F9EB5; font-size: 11px; text-transform: uppercase; border-bottom: 1px solid rgba(255,255,255,0.1); }
                .lab-table td { padding: 12px 10px; color: #E0E0E0; border-bottom: 1px solid rgba(255,255,255,0.05); }
                .locked-row { opacity: 0.5; filter: grayscale(100%); pointer-events: none; }
                .share-dropdown-item { padding: 10px 15px; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.05); font-size: 13px; color: #E0E0E0; display: flex; align-items: center; justify-content: space-between; }
                .share-dropdown-item:hover { background: rgba(108, 93, 211, 0.2); color: #fff; }
                .delete-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(3px); z-index: 99999; display: flex; align-items: center; justify-content: center; }
                .delete-modal { background: #151821; border: 1px solid rgba(229,62,62,0.3); border-radius: 16px; padding: 30px; width: 320px; text-align: center; box-shadow: 0 20px 40px rgba(0,0,0,0.8); }

                .detailed-board-table th { background: rgba(0,0,0,0.3); padding: 8px; text-align: center; color: #8F9EB5; font-size: 11px; border-bottom: 1px solid rgba(255,255,255,0.1); }
                .detailed-board-table td { padding: 8px; text-align: center; font-size: 13px; border-bottom: 1px solid rgba(255,255,255,0.05); }
            `}</style>

            {itemToDelete && (
                <div className="delete-modal-overlay" onClick={() => setItemToDelete(null)}>
                    <div className="delete-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize: 40, marginBottom: 10 }}>🗑️</div>
                        <h2 style={{ margin: '0 0 10px 0', color: '#fff' }}>Delete Item?</h2>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setItemToDelete(null)} style={{ flex: 1, padding: 10, background: 'rgba(255,255,255,0.05)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={() => { deleteAnalysisBoard(itemToDelete); setItemToDelete(null); }} style={{ flex: 1, padding: 10, background: '#E53E3E', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="ContentWrapper" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px', minHeight: 0, overflow: 'hidden' }}>
                <div className="unified-backdrop" style={{ maxWidth: activeItem ? (activeItem.type==='collection' ? 1400 : 1000) : 1200, maxHeight: '100%', minHeight: 0 }}>
                    
                    {/* HEADER */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h1 style={{ margin: 0, fontSize: 22, color: '#fff' }}>{activeItem ? activeItem.boardName : "Hand Lab"}</h1>
                        
                        {activeItem ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                                {activeItem.collectionType === 'batch' && (
                                    <button onClick={() => downloadLinFile(activeItem.rawLinFile, `${activeItem.boardName}.lin`)} style={{ background: 'transparent', color: '#F39C12', border: '1px solid #F39C12', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>Download LIN</button>
                                )}
                                
                                {(isAdmin || (activeItemDoc && activeItemDoc.createdBy === user.email)) && (
                                    <div style={{ position: 'relative' }}>
                                        {isSharing ? (
                                            <div style={{ display: 'flex', alignItems: 'center', background: '#11131A', border: '1px solid #6C5DD3', borderRadius: 8, padding: '4px 8px' }}>
                                                <input autoFocus placeholder="Search user..." value={shareQuery} onChange={e => setShareQuery(e.target.value)} style={{ border: 'none', background: 'transparent', color: '#fff', width: 140, outline: 'none', fontSize: 13 }} />
                                                <button onClick={() => {setIsSharing(false); setShareQuery('');}} style={{ background: 'none', color: '#8F9EB5', border: 'none', cursor: 'pointer', marginLeft: 5 }}>✕</button>
                                                
                                                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 5, width: 250, background: '#1A1D26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden', zIndex: 100, boxShadow: '0 10px 30px rgba(0,0,0,0.8)' }}>
                                                    {shareQuery && filteredUsers.map(u => (
                                                        <div key={`s_${u.uid}`} className="share-dropdown-item">
                                                            <span style={{fontWeight: 'bold'}}>{u.name || u.email.split('@')[0]}</span>
                                                            <button onClick={() => executeShareAction(u.email, false)} style={{background:'#6C5DD3', color:'#fff', border:'none', borderRadius:4, padding:'4px 8px', fontSize:10, cursor: 'pointer'}}>Add</button>
                                                        </div>
                                                    ))}
                                                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '5px 10px', fontSize: 10, color: '#8F9EB5', textTransform: 'uppercase', letterSpacing: 1, borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Currently Shared With</div>
                                                    {activeItemDoc.allowedUsers.filter(e => e !== activeItemDoc.createdBy).map(email => (
                                                        <div key={`e_${email}`} className="share-dropdown-item">
                                                            <span style={{fontSize: 12, color: '#A0AABF'}}>{email}</span>
                                                            <button onClick={() => executeShareAction(email, true)} style={{background:'rgba(229,62,62,0.2)', color:'#E53E3E', border:'1px solid rgba(229,62,62,0.4)', borderRadius:4, padding:'4px 8px', fontSize:10, cursor: 'pointer'}}>Revoke</button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <button onClick={() => setIsSharing(true)} style={{ background: 'transparent', color: '#A0AABF', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>🔗 Share</button>
                                        )}
                                    </div>
                                )}

                                {/* SAVE BUTTON (Available to all logged-in users so they can fork) */}
                                <div style={{ position: 'relative' }}>
                                    <button onClick={() => document.getElementById('hidden-save-btn')?.click()} style={{ background: 'rgba(74, 222, 128, 0.15)', color: '#4ADE80', border: '1px solid rgba(74, 222, 128, 0.3)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>💾 Save</button>

                                    {showSaveModal && (
                                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: '#1A1D26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 20, width: 260, zIndex: 100 }}>
                                            <input className="DarkInput" value={saveName} onChange={e => {setSaveName(e.target.value); setConfirmOverwrite(false);}} style={{width: '100%', marginBottom: 15}} />
                                            {saveName.trim() === activeItem.boardName ? (
                                                confirmOverwrite ? (
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button onClick={() => setConfirmOverwrite(false)} style={{ flex: 1, padding: '6px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                                                        <button onClick={executeSave} style={{ flex: 1, padding: '6px', background: '#E53E3E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Yes</button>
                                                    </div>
                                                ) : <button onClick={() => setConfirmOverwrite(true)} style={{ width: '100%', padding: '10px', background: 'transparent', color: '#E0E0E0', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer' }}>Overwrite</button>
                                            ) : <button onClick={executeSave} style={{ width: '100%', padding: '10px', background: '#6C5DD3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Save as New</button>}
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => {setActiveItem(null); setItemId(null); setIsSharing(false); setActiveBoardPlay(null);}} style={{ background: 'rgba(229, 62, 62, 0.15)', color: '#E53E3E', border: '1px solid rgba(229, 62, 62, 0.3)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>Close</button>
                            </div>
                        ) : null}
                    </div>

                    {/* --- LOBBY VIEW --- */}
                    {!activeItem ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15, width: '100%', flex: 1, minHeight: 550, maxHeight: 550}}>
                            
                            {/* ROW 1: MY LOBBY (Stretches to fill space and scrolls internally) */}
                            <div className="analysis-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                
                                {/* LOBBY HEADER WITH FILTERS & IMPERSONATION */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexShrink: 0, flexWrap: 'wrap', gap: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <h2 style={{ margin: 0, fontSize: 18, color: '#E0E0E0' }}>
                                            {impersonateEmail ? `Lobby: ${impersonateEmail.split('@')[0]}` : 'My Lobby'}
                                        </h2>
                                        
                                        {/* FILTER PILLS */}
                                        <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', borderRadius: 20, padding: 2 }}>
                                            {['all', 'mine', 'shared'].map(mode => (
                                                <button key={mode} onClick={() => setFilterMode(mode)} style={{ background: filterMode === mode ? '#6C5DD3' : 'transparent', color: filterMode === mode ? '#fff' : '#8F9EB5', border: 'none', padding: '2px 10px', fontSize: 10, fontWeight: 'bold', borderRadius: 18, cursor: 'pointer', textTransform: 'capitalize', transition: '0.2s' }}>
                                                    {mode}
                                                </button>
                                            ))}
                                        </div>
                                        {!isAdmin && <span style={{fontSize: 11, color: canSave ? '#8F9EB5' : '#E53E3E', fontWeight: 'bold', marginLeft: 5}}>{mySavedCount} / 25 Saved</span>}
                                    </div>

                                    {/* ADMIN IMPERSONATION UI */}
                                    {isAdmin && (
                                        <div style={{ position: 'relative' }}>
                                            {impersonateEmail ? (
                                                <button onClick={() => {setImpersonateEmail(""); setImpersonateSearch("");}} style={{ background: 'rgba(229,62,62,0.15)', color: '#E53E3E', border: '1px solid rgba(229,62,62,0.3)', padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 'bold', cursor: 'pointer' }}>
                                                    Stop Impersonating
                                                </button>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: 6, padding: '4px 8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                                    <span style={{fontSize: 10, color: '#8F9EB5', marginRight: 6, fontWeight: 'bold'}}>🕵️ VIEW AS:</span>
                                                    <input 
                                                        placeholder="Search user..." value={impersonateSearch} 
                                                        onChange={e => setImpersonateSearch(e.target.value)} 
                                                        onFocus={() => setIsImpersonatingUI(true)}
                                                        style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 11, width: 100, outline: 'none' }}
                                                    />
                                                    {isImpersonatingUI && impersonateSearch && (
                                                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 5, background: '#1A1D26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden', zIndex: 100, width: 220, boxShadow: '0 10px 25px rgba(0,0,0,0.8)' }}>
                                                            {impersonateSearchResults.map(u => (
                                                                <div key={`imp_${u.uid}`} onClick={() => {setImpersonateEmail(u.email); setIsImpersonatingUI(false);}} className="share-dropdown-item">
                                                                    {u.name || u.email.split('@')[0]}
                                                                </div>
                                                            ))}
                                                            {impersonateSearchResults.length === 0 && <div style={{padding: 10, fontSize: 11, color: '#888', textAlign: 'center'}}>No users found.</div>}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {/* RENDER FILTERED ITEMS */}
                                <div className="CustomScroll" style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflowY: 'auto', paddingRight: 5 }}>
                                    {filteredItems.map(item => {
                                        const state = item.data;
                                        return (
                                            <div key={item.id} style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                                <button className="board-btn" onClick={() => {setItemId(item.id); setActiveItem(state);}}>
                                                    <span style={{ fontWeight: 'bold', fontSize: 14 }}>{state.boardName}</span>
                                                    <span style={{ fontSize: 11, color: '#8F9EB5' }}>{state.type === 'collection' ? `${state.collectionType.toUpperCase()} • ${state.boards?.length} Bds` : `Board ${state.boardNum}`}</span>
                                                </button>
                                                {(isAdmin || item.docData?.createdBy === effectiveEmail) && (
                                                    <button onClick={() => setItemToDelete(item.id)} style={{ background: 'rgba(229, 62, 62, 0.1)', color: '#E53E3E', border: '1px solid rgba(229, 62, 62, 0.3)', padding: '0 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {filteredItems.length === 0 && <div style={{color: '#666', fontStyle: 'italic', fontSize: 12, textAlign: 'center', marginTop: 20}}>No boards found.</div>}
                                </div>
                            </div>

                            {/* ROW 2: CREATION TOOLS (Sits at the bottom) */}
                            <div className="lobby-grid" style={{ flexShrink: 0 }}>
                                {/* COLUMN 1: SINGLE BOARD */}
                                <div className="analysis-card" style={{ flex: 1 }}>
                                    <h2 style={{ margin: 0, fontSize: 18, color: '#fff' }}>Start Single Board</h2>
                                    <div style={{ display: 'flex', gap: 15 }}>
                                        <div style={{ flex: 1 }}><label className="input-label">Board #</label><input className="DarkInput" value={setupNum} onChange={e => setSetupNum(e.target.value)} style={{ width: '100%', margin: 0 }} /></div>
                                        <div style={{ flex: 2 }}><label className="input-label">Name</label><input className="DarkInput" value={setupName} onChange={e => setSetupName(e.target.value)} style={{ width: '100%', margin: 0 }} /></div>
                                    </div>
                                    <div><label className="input-label">BBO Link</label><input className="DarkInput" value={bboLink} onChange={e => setBboLink(e.target.value)} style={{ width: '100%', margin: 0 }} /></div>
                                    <div style={{ display: 'flex', gap: 10, marginTop: 'auto' }}>
                                        <button className="create-btn" onClick={handleCreateNew} style={{ flex: 1, opacity: canSave ? 1 : 0.5 }}>Save to Cloud</button>
                                        <button className="create-btn" onClick={handleCreateLocal} style={{ flex: 1, background: 'transparent', border: '1px solid #6C5DD3', color: '#A395E9' }}>Play Locally</button>
                                    </div>
                                </div>

                                {/* COLUMN 2: COLLECTIONS & IMPORTS */}
                                <div className="analysis-card" style={{ flex: 1 }}>
                                    <h2 style={{ margin: 0, fontSize: 18, color: '#fff' }}>Collections & Imports</h2>
                                    <div style={{display: 'flex', gap: 10, flexDirection: 'column', height: '100%'}}>
                                        
                                        <div style={{display: 'flex', flexDirection: 'column', paddingBottom: isAdmin ? 10 : 0, borderBottom: isAdmin ? '1px dashed rgba(255,255,255,0.1)' : 'none', flex: 1}}>
                                            <label className="input-label">Import Match (.LIN)</label>
                                            <div style={{ display: 'flex', gap: 10, flex: 1 }}>
                                                <input className="DarkInput" value={importName} onChange={e=>setImportName(e.target.value)} placeholder="Match Name" style={{flex: 1, margin: 0}} />
                                                <textarea className="DarkInput" value={importLin} onChange={e=>setImportLin(e.target.value)} placeholder="Paste LIN..." style={{flex: 2, resize: 'none', margin: 0}} />
                                                <button className="create-btn" onClick={handleImportLin} style={{background: '#26A65B', opacity: canSave ? 1 : 0.5, width: 'auto', padding: '0 15px'}}>Import</button>
                                            </div>
                                        </div>

                                        {isAdmin && (
                                            <div>
                                                <label className="input-label" style={{color: '#F39C12'}}>Batch Generator (Admin)</label>
                                                <div style={{display: 'flex', gap: 10}}>
                                                    <input className="DarkInput" value={genName} onChange={e => setGenName(e.target.value)} placeholder="Batch Name" style={{ flex: 2, margin: 0 }} />
                                                    <input type="number" className="DarkInput" value={genStart} onChange={e=>setGenStart(e.target.value)} placeholder="Start" style={{flex: 1, margin: 0}}/>
                                                    <input type="number" className="DarkInput" value={genEnd} onChange={e=>setGenEnd(e.target.value)} placeholder="End" style={{flex: 1, margin: 0}}/>
                                                    <button className="create-btn" onClick={handleGenerateBatch} disabled={isGenerating} style={{ background: '#F39C12', width: 'auto', padding: '0 15px' }}>{isGenerating ? "..." : "Generate"}</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                    /* --- SINGLE BOARD VIEW --- */
                    ) : activeItem.type === 'single' ? (
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}>
                            <SingleHandViewer 
                                details={activeItem.details} boardNum={activeItem.boardNum} contractStr={activeItem.contractStr} 
                                label={activeItem.boardName}
                                onSaveLabHand={(l,b,p,c) => handleManualSave(l,b,p,c)}
                            />
                        </div>

                    /* --- COLLECTION VIEW (Matches/Batches) --- */
                    ) : (
                        <div className="CustomScroll" style={{ overflow: 'auto', flex: 1, minHeight: 0, paddingRight: 5 }}>
                            <table className="detailed-board-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 600 }}>
                                <thead>
                                    <tr>
                                        <th style={{width: '60px', paddingBottom: 10}}>Bd</th>
                                        {activeItem.collectionType === 'match' ? (
                                            <>
                                                <th style={{paddingBottom: 10}}>{activeItem.linTeam1 || "Home"}</th>
                                                <th style={{paddingBottom: 10}}>{activeItem.linTeam2 || "Away"}</th>
                                                <th style={{width: '100px', paddingBottom: 10}}>Score<br/><span style={{fontSize: 9, color: '#666'}}>(Home | Away)</span></th>
                                                <th style={{width: '60px', paddingBottom: 10, color: WIN_COLOR}}>+ IMPs</th>
                                                <th style={{width: '60px', paddingBottom: 10, color: LOSE_COLOR}}>- IMPs</th>
                                            </>
                                        ) : (
                                            <th style={{paddingBottom: 10}}></th> /* Invisible Spacer */
                                        )}
                                        <th style={{width: '130px', paddingBottom: 10, textAlign: 'right'}}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        let totalPlus = 0;
                                        let totalMinus = 0;

                                        const rows = activeItem.boards.map((b, i) => {
                                            const isLocked = !b.isVisible && !isAdmin;
                                            const isExpanded = activeBoardPlay === i;
                                            const isActive = isExpanded; // Used for triggering animations
                                            
                                            // Construct proper lineup objects from the flat LIN player array
                                            let t1Data = {}; let t2Data = {};
                                            if (activeItem.collectionType === 'match' && activeItem.players) {
                                                // BBO LIN standard order: S, W, N, E
                                                t1Data = { south: activeItem.players[0], west: activeItem.players[1], north: activeItem.players[2], east: activeItem.players[3], name: activeItem.linTeam1 };
                                                t2Data = { south: activeItem.players[4], west: activeItem.players[5], north: activeItem.players[6], east: activeItem.players[7], name: activeItem.linTeam2 };
                                            }
                                            
                                            // Keep track of totals for imported matches
                                            const myBoardImps = (b.imps1 || 0) - (b.imps2 || 0);
                                            if (activeItem.collectionType === 'match') {
                                                if (myBoardImps > 0) totalPlus += myBoardImps;
                                                if (myBoardImps < 0) totalMinus += Math.abs(myBoardImps);
                                            }

                                            return (
                                                <React.Fragment key={i}>
                                                    <tr className={isLocked ? "locked-row" : ""} style={{background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}}>
                                                        <td style={{color: '#8F9EB5', fontWeight: 'bold'}}>{b.boardNum}</td>
                                                        
                                                        {activeItem.collectionType === 'match' ? (
                                                            <>
                                                                <td>{formatContract(b.room1)}</td>
                                                                <td>{formatContract(b.room2)}</td>
                                                                <td style={{fontSize: 11, color: '#A0AABF'}}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '65px', margin: '0 auto' }}>
                                                                        <span style={{ flex: 1, textAlign: 'right', color:  '#fff' }}>{b.raw1 || 0}</span>
                                                                        <span style={{ color: 'rgba(255,255,255,0.2)', padding: '0 4px' }}>|</span>
                                                                        <span style={{ flex: 1, textAlign: 'right', color: '#fff' }}>{b.raw2 || 0}</span>
                                                                    </div>
                                                                </td>
                                                                <td style={{fontWeight: 'bold', color: myBoardImps > 0 ? WIN_COLOR : TIE_COLOR}}>{myBoardImps > 0 ? myBoardImps : '-'}</td>
                                                                <td style={{fontWeight: 'bold', color: myBoardImps < 0 ? LOSE_COLOR : TIE_COLOR}}>{myBoardImps < 0 ? Math.abs(myBoardImps) : '-'}</td>
                                                            </>
                                                        ) : (
                                                            <td></td> /* Matches the invisible spacer in the header */
                                                        )}

                                                        <td style={{textAlign: 'right'}}>
                                                            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center'}}>
                                                                {isAdmin && activeItem.collectionType === 'batch' && (
                                                                    <button 
                                                                        onClick={() => toggleBoardVisibility(i)} 
                                                                        style={{ 
                                                                            background: b.isVisible ? 'rgba(229, 62, 62, 0.15)' : 'rgba(38, 166, 91, 0.15)', 
                                                                            color: b.isVisible ? '#E53E3E' : '#26A65B', 
                                                                            border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 10, fontWeight: 'bold', cursor: 'pointer' 
                                                                        }}
                                                                    >
                                                                        {b.isVisible ? "Hide" : "Show"}
                                                                    </button>
                                                                )}
                                                                <button 
                                                                    disabled={isLocked} onClick={() => setActiveBoardPlay(isExpanded ? null : i)}
                                                                    style={{ background: isExpanded ? 'rgba(108, 93, 211, 0.2)' : '#2D2B3F', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 'bold', cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    {isLocked ? "🔒 Hidden" : isExpanded ? "x Close" : "Expand"}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            {/* CRITICAL: maxWidth: 0 prevents the expanded content from stretching the table headers! */}
                                                            <td colSpan={activeItem.collectionType === 'match' ? 7 : 3} style={{padding: 0, maxWidth: 0}}>
                                                                <div style={{padding: '15px 0', borderBottom: '2px solid rgba(108, 93, 211, 0.3)', width: '100%', overflowX: 'auto'}}>
                                                                    {activeItem.collectionType === 'match' ? (
                                                                        <HandAnalyzer 
                                                                            board={b} isActive={isActive} t1Data={t1Data} t2Data={t2Data}
                                                                            t1Color="#4ADE80" t2Color="#F87171" swapRooms={false}
                                                                        />
                                                                    ) : (
                                                                        <SingleHandViewer 
                                                                            details={b.details} boardNum={b.boardNum} contractStr={b.contractStr} 
                                                                            label={`Board ${b.boardNum}`}
                                                                            onSaveLabHand={(l,bid,p,c) => handleManualSave(l,bid,p,c, i)}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        });

                                        return (
                                            <>
                                                {rows}
                                                {activeItem.collectionType === 'match' && (
                                                    <tr style={{background: 'rgba(0,0,0,0.4)', borderTop: '2px solid rgba(255,255,255,0.1)'}}>
                                                        <td colSpan={4} style={{textAlign: 'right', fontWeight: 'bold', paddingRight: 20, color: '#fff', fontSize: 14}}>TOTAL IMPs:</td>
                                                        <td style={{color: WIN_COLOR, fontWeight: 'bold', fontSize: 14}}>{totalPlus}</td>
                                                        <td style={{color: LOSE_COLOR, fontWeight: 'bold', fontSize: 14}}>{totalMinus}</td>
                                                        <td></td>
                                                    </tr>
                                                )}
                                            </>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HandLab;